import { datadogRum } from "@datadog/browser-rum";
import { isAudit } from "PFApp/activities/helpers";
import { getOriginalResource } from "PFApp/booking/parts/helpers";
import { Day } from "PFApp/booking/parts/overview/booking_module_calendar/booking_calendar/booking_calendar_store";
import { useBookingDndApiContext } from "PFApp/booking/parts/overview/booking_module_calendar/context/booking_dnd_context";
import { RowsWithRefs, RowWithRef, useCalendarRootActions } from "PFApp/booking/store";
import { CalendarMode, Row } from "PFApp/booking/types";
import { CalendarRoot } from "PFApp/booking/types/calendar_root";
import { ProfileBasic } from "PFApp/booking/types/row";
import { useBookingPastDaysLimit, useTemplateFind } from "PFApp/hooks";
import {
  DROP_AREA_OVERLAY_ID,
  useDropAreaOverlayRef
} from "PFComponents/drop_area_overlay/use_drop_area_overlay_ref";
import { ProjectItemActivity } from "PFCore/hooks/queries/bookings/projects/use_projects_entries";
import { useDateFormatter } from "PFCore/hooks/use_date_formatter";
import { useIsUnbiasedMode } from "PFCore/hooks/use_is_unbiased_mode";
import { Booking } from "PFTypes";
import { RefObject, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useReassignActionApi } from "../../../../booking_module_calendar/context/reassign_action_context";
import { calculateBarDimensions } from "../../bars.utils";
import getUpdatedDates from "./get_updated_dates";
import { DnDMode } from "./index";
import {
  calculateDropZoneHeight,
  calculateHorizontalDragMove,
  canDragBooking,
  getBookingDatesDiff,
  getDistanceFromRowStartToClientX,
  getMaxVerticalTop,
  getNumberOfDraggedDays,
  getSourceProfileOriginal,
  getSuspendedProfileDetails,
  getTargetProfileId,
  getTargetRowWithRef,
  isDroppableArea,
  shouldMoveBookingHorizontally,
  shouldMoveBookingXY,
  updateHTMLDivElementPosition
} from "./use_booking_bar_dnd.utils";
import { Position, useDragAndDrop } from "./use_drag_and_drop";
import useUpdateBooking from "./use_update_booking";

type DraggingDates = { start: Date; end: Date };

export type DropEndData = { daysDiff?: number };

type RowsData = {
  row: Row;
};

type BookingRefs = {
  barRef: RefObject<HTMLDivElement | null>;
  leftEdgeRef: RefObject<HTMLDivElement | null>;
  rightEdgeRef: RefObject<HTMLDivElement | null>;
};

type BookingPosition = {
  left: number;
  width: number;
};

type UseBookingBarDnD = {
  start: string;
  end: string;
  booking: Booking;
  bookingPosition: BookingPosition;
  onDragStart: () => void;
  onDragMoveEnd: () => void;
  onDropEnd: (data?: DropEndData) => void;
  days: Day[];
  bookingRefs: BookingRefs;
  rowsData: RowsData;
  calendarMode: CalendarMode;
  calendarRoot: CalendarRoot;
  onLoadingChange: (value: boolean) => void;
};

export const useBookingBarDnD = ({
  start,
  end,
  booking,
  bookingPosition: { left, width },
  onDragStart,
  onDragMoveEnd,
  onDropEnd,
  bookingRefs: { barRef, leftEdgeRef, rightEdgeRef },
  days,
  rowsData: { row },
  calendarMode,
  calendarRoot,
  onLoadingChange
}: UseBookingBarDnD) => {
  const { t } = useTranslation("bookingModule");
  const draggingMode = useRef<DnDMode | null>(null);
  const draggedDays = useRef<number>(0);
  const draggingOriginClick = useRef<Position | null>(null);
  const isUnbiasedMode = useIsUnbiasedMode();

  const INITIAL_DRAGGING_DATES = { start: new Date(start), end: new Date(end) };
  const draggingDates = useRef<DraggingDates>(INITIAL_DRAGGING_DATES);

  const { getRowsWithRefs } = useCalendarRootActions(calendarRoot);
  const rowsWithRefsRef = useRef<RowsWithRefs<Row>>(new Map());
  const getSourceRowWithRef = () => rowsWithRefsRef.current.get(row.key);
  const getRows = () => Array.from(rowsWithRefsRef.current.values()).map(({ row }) => row);

  const sourceRowZIndexRef = useRef<string | undefined>(undefined);
  const targetRowRef = useRef<RowWithRef<Row> | null>(null);

  const { dropZoneElementRef } = useDropAreaOverlayRef();

  const { formatDatabaseDateTime } = useDateFormatter();
  const { openModal: openReassignModal } = useReassignActionApi();
  const bookingPastDaysLimit = useBookingPastDaysLimit();
  const { checkCanDragVertically } = useBookingDndApiContext();
  const canDragVerticallyBaseCheck = checkCanDragVertically(booking);

  // eslint-disable-next-line react-hooks/refs
  const targetRow = targetRowRef.current
    ? getRows().find(({ key }) => key === targetRowRef.current?.row.key) // eslint-disable-line react-hooks/refs
    : undefined;
  const targetRowProfile = targetRow && targetRow.type === "profile" ? targetRow : null;

  const findTemplate = useTemplateFind();
  const isAuditBooking = booking ? isAudit(findTemplate({ id: booking.activity?.template_id })) : false;

  const setInitialBookingDimensions = () => {
    updateHTMLDivElementPosition(barRef.current, {
      left: `${left}px`,
      width: `${width}px`
    });
  };

  const { updateBooking } = useUpdateBooking({
    booking,
    onError: setInitialBookingDimensions,
    onLoadingChange
  });

  const removeDropZoneFromRow = (rowWithRef?: RowWithRef<Row> | null) => {
    const sourceRowWithRef = getSourceRowWithRef();
    document.getElementById(DROP_AREA_OVERLAY_ID)?.remove();
    if (rowWithRef && rowWithRef.row.key !== sourceRowWithRef?.row.key) {
      rowWithRef.rowRef && rowWithRef.rowRef.style.removeProperty("z-index");
    }
  };

  const handleDragStart = (initialPosition: Position) => {
    const sourceRowWithRef = getSourceRowWithRef();
    draggingOriginClick.current = {
      x: getDistanceFromRowStartToClientX(initialPosition.x, sourceRowWithRef?.rowRef ?? undefined),
      y: initialPosition.y
    };

    onDragStart();
  };

  const handleDragMove = (
    clientPosition: Position,
    canDragHorizontally: boolean,
    canDragVertically: boolean
  ) => {
    const sourceRowWithRef = getSourceRowWithRef();
    const horizontalDragMoveData = calculateHorizontalDragMove({
      booking,
      clientX: clientPosition.x,
      days,
      draggedDays,
      draggingMode,
      draggingOriginClick,
      bookingPastDaysLimit,
      rowRef: sourceRowWithRef?.rowRef ?? undefined
    });

    if (canDragVertically) {
      targetRowRef.current = getTargetRowWithRef(
        rowsWithRefsRef.current,
        clientPosition.y,
        getRows(),
        calendarMode
      );
    }

    if (!horizontalDragMoveData || !canDragHorizontally) {
      onDragMoveEnd();
      return;
    }

    const { draggedDaysNumber, startDate, endDate } = horizontalDragMoveData;

    draggedDays.current = draggedDaysNumber;
    draggingDates.current = {
      start: startDate,
      end: endDate
    };

    onDragMoveEnd();
  };

  const updateBookingHorizontalPosition = (clientPosition: Position, canDragHorizontally: boolean) => {
    if (!draggingMode.current || !draggingOriginClick.current || !canDragHorizontally) {
      return;
    }

    const sourceRowWithRef = getSourceRowWithRef();
    const { draggedDaysNumber } = getNumberOfDraggedDays({
      currentMouseX: clientPosition.x,
      originClickX: draggingOriginClick.current.x,
      rowRef: sourceRowWithRef?.rowRef ?? undefined,
      days
    });

    const { startDate, endDate } = getUpdatedDates(
      draggingMode.current,
      draggedDaysNumber,
      booking?.start_date,
      booking?.end_date,
      days,
      bookingPastDaysLimit
    );

    const { left, width: barWidth } = calculateBarDimensions(
      startDate.toISOString(),
      endDate.toISOString(),
      days
    );

    updateHTMLDivElementPosition(barRef.current, {
      left: `${left}px`,
      width: `${barWidth}px`
    });
  };

  const updateBookingVerticalPosition = (
    clientPosition: Position,
    canDragHorizontally: boolean,
    canDragVertically: boolean
  ) => {
    if (!draggingMode.current || !draggingOriginClick.current || !canDragVertically) {
      return;
    }

    const moveDropZone = (clientPosition: Position, canDragHorizontally: boolean) => {
      if (!draggingOriginClick.current || !draggingMode.current) {
        return;
      }

      const sourceRowWithRef = getSourceRowWithRef();
      const rows = getRows();

      const { draggedDaysNumber } = getNumberOfDraggedDays({
        currentMouseX: clientPosition.x,
        originClickX: draggingOriginClick.current.x,
        rowRef: sourceRowWithRef?.rowRef ?? undefined,
        days
      });

      const { startDate, endDate } = getUpdatedDates(
        draggingMode.current,
        canDragHorizontally ? draggedDaysNumber : 0,
        booking?.start_date,
        booking?.end_date,
        days,
        bookingPastDaysLimit
      );

      const { left } = calculateBarDimensions(startDate.toISOString(), endDate.toISOString(), days);

      const currentTargetRowWithRef = getTargetRowWithRef(
        rowsWithRefsRef.current,
        clientPosition.y,
        rows,
        calendarMode
      );

      if (currentTargetRowWithRef?.row.key !== targetRowRef.current?.row.key) {
        removeDropZoneFromRow(targetRowRef.current);
      }
      const { isReturnedToSourceRow: isBackToSourceRow, isDroppable } = isDroppableArea({
        targetRowWithRef: currentTargetRowWithRef,
        draggingRow: targetRowRef,
        rows,
        sourceRowWithRef,
        calendarMode,
        calendarRoot
      });

      if (dropZoneElementRef.current && currentTargetRowWithRef?.rowRef && isDroppable) {
        removeDropZoneFromRow(currentTargetRowWithRef);

        const dropZoneHeight = calculateDropZoneHeight(
          currentTargetRowWithRef,
          rows,
          rowsWithRefsRef.current
        );
        updateHTMLDivElementPosition(dropZoneElementRef.current, {
          height: `${dropZoneHeight ?? 0}px`,
          width: `${barRef.current?.getBoundingClientRect().width ?? 0}px`,
          left: `${left}px`
        });

        currentTargetRowWithRef?.rowRef && currentTargetRowWithRef.rowRef.style.setProperty("z-index", "1");
        currentTargetRowWithRef?.rowRef &&
          currentTargetRowWithRef.rowRef.appendChild(dropZoneElementRef.current);
      } else if (isBackToSourceRow) {
        removeDropZoneFromRow(currentTargetRowWithRef);
      }
    };

    moveDropZone(clientPosition, canDragHorizontally);

    const { maxTopUp, maxTopDown } = getMaxVerticalTop({
      rowsWithRefs: rowsWithRefsRef.current,
      initialClientPositionY: draggingOriginClick.current.y
    });
    const newTop = Math.min(
      maxTopDown,
      Math.max(maxTopUp, clientPosition.y - (draggingOriginClick.current?.y ?? 0))
    );
    updateHTMLDivElementPosition(barRef.current, {
      top: `${newTop}px`
    });
  };

  const updateBookingXYPosition = (
    clientPosition: Position,
    canDragHorizontally: boolean,
    canDragVertically: boolean
  ) => {
    updateBookingHorizontalPosition(clientPosition, canDragHorizontally);
    updateBookingVerticalPosition(clientPosition, canDragHorizontally, canDragVertically);
  };

  const clearVerticalAction = (shouldResetPosition = false) => {
    const sourceRowWithRef = getSourceRowWithRef();
    removeDropZoneFromRow(targetRowRef.current);
    targetRowRef.current = null;
    if (shouldResetPosition) {
      barRef.current?.style.removeProperty("top");
      updateHTMLDivElementPosition(barRef.current, {
        left: `${left}px`
      });
    }
    sourceRowWithRef?.rowRef?.style.setProperty("z-index", sourceRowZIndexRef.current ?? "");
    draggingOriginClick.current = null;
    sourceRowZIndexRef.current = undefined;
  };

  const handleDropEnd = (shouldResetPosition = false) => {
    draggingMode.current = null;
    draggingOriginClick.current = null;
    draggedDays.current = 0;

    clearVerticalAction(shouldResetPosition);
  };

  const handleDrop = (clientPosition: Position, dndMode: DnDMode) => {
    if (!booking || !draggingMode.current) {
      handleDropEnd();
      onDropEnd();
      return;
    }

    const sourceRowWithRef = getSourceRowWithRef();
    const rows = getRows();

    const { start, end } = draggingDates.current;

    const daysDiff = getBookingDatesDiff(booking, start, end);
    const currentTargetRowWithRef = getTargetRowWithRef(
      rowsWithRefsRef.current,
      clientPosition.y,
      rows,
      calendarMode
    );

    const { isDroppable } = isDroppableArea({
      targetRowWithRef: currentTargetRowWithRef,
      draggingRow: targetRowRef,
      rows,
      sourceRowWithRef,
      calendarMode,
      calendarRoot
    });

    new Promise<void>((resolve) => setTimeout(resolve, 0)) //workaround to not trigger click on bar when dragging to initial position
      .then(() => {
        if (canDragVerticallyBaseCheck && isDroppable) {
          const targetRow = rows.find(
            ({ key }) => targetRowRef.current && targetRowRef.current?.row.key === key
          );

          const sourceProfileOriginal = getSourceProfileOriginal(row, rows, calendarMode) as ProfileBasic;
          const sourceProfileId = sourceProfileOriginal?.id;
          const sourceProfileParentRow = rows.find(({ key }) => row.parents[0] === key);
          const sourceProfileParentActivity =
            sourceProfileParentRow &&
            (getOriginalResource(sourceProfileParentRow, calendarRoot) as ProjectItemActivity);

          const sourceProfileIsUnbiasedMode = isUnbiasedMode(sourceProfileParentActivity?.reviewers);

          const targetProfileId = getTargetProfileId(row, targetRow, rows, calendarMode);
          const targetProfileParentRow = rows.find(({ key }) => targetRow?.key === key);
          const targetProfileParentActivity =
            targetProfileParentRow &&
            (getOriginalResource(targetProfileParentRow, calendarRoot) as ProjectItemActivity);
          const targetProfileIsUnbiasedMode = isUnbiasedMode(targetProfileParentActivity?.reviewers);

          onDropEnd({ daysDiff });

          if (dndMode === DnDMode.HorizontalVertical && sourceProfileId && targetProfileId) {
            const isSuspended =
              "suspendedAt" in sourceProfileOriginal ? !!sourceProfileOriginal.suspendedAt : false;
            openReassignModal({
              activityName: booking.activity?.name,
              sourceProfileId: Number(sourceProfileId),
              sourceProfileIsUnbiasedMode,
              targetProfileIsUnbiasedMode,
              targetProfileId: Number(targetProfileId),
              suspendedProfileDetails:
                isSuspended && calendarMode === CalendarMode.Projects
                  ? getSuspendedProfileDetails(sourceProfileOriginal as ProfileBasic)
                  : undefined,
              bookings: [booking],
              onClose: () => {
                handleDropEnd(true);
              },
              onSubmit: async () => {
                if (daysDiff !== 0) {
                  await updateBooking({
                    startDate: formatDatabaseDateTime(start.getTime()),
                    endDate: formatDatabaseDateTime(end.getTime())
                  });
                }
                handleDropEnd();
              },
              onError: setInitialBookingDimensions
            });
          }
        } else if (daysDiff !== 0) {
          return updateBooking({
            startDate: formatDatabaseDateTime(start.getTime()),
            endDate: formatDatabaseDateTime(end.getTime())
          }).then(() => {
            handleDropEnd();
            onDropEnd({ daysDiff });
          });
        } else {
          handleDropEnd(true);
          onDropEnd({ daysDiff });
        }
      });
  };

  const { handleMouseDown: handleMouseDownStartEndMode, isDragging: isDraggingStartEndMode } = useDragAndDrop(
    {
      draggableElementRef: barRef,
      onDragStart: (initialPosition) => {
        rowsWithRefsRef.current = getRowsWithRefs();
        const sourceRowWithRef = getSourceRowWithRef();
        draggingMode.current = canDragVerticallyBaseCheck ? DnDMode.HorizontalVertical : DnDMode.Horizontal;

        sourceRowZIndexRef.current = sourceRowWithRef?.rowRef?.style.zIndex;
        sourceRowWithRef?.rowRef?.style.setProperty("z-index", "2");
        handleDragStart(initialPosition);
      },
      onDragMove: (clientPosition) => {
        const { canDragHorizontally, canDragVertically } = canDragBooking({
          isAuditBooking,
          sourceRowWithRef: getSourceRowWithRef(),
          targetRowRef: targetRowRef.current,
          draggedDays: draggedDays.current,
          canDragVerticallyBaseCheck
        });

        handleDragMove(clientPosition, canDragHorizontally, canDragVertically);
      },
      onDrop: (clientPosition) => {
        datadogRum.addAction(t("dragAndDropBooking.bookingMove", { lng: "en" }));
        handleDrop(clientPosition, DnDMode.HorizontalVertical);
      },
      customDragOperation: (clientPosition) => {
        const { canDragHorizontally, canDragVertically } = canDragBooking({
          isAuditBooking,
          sourceRowWithRef: getSourceRowWithRef(),
          targetRowRef: targetRowRef.current,
          draggedDays: draggedDays.current,
          canDragVerticallyBaseCheck
        });

        updateBookingXYPosition(clientPosition, canDragHorizontally, canDragVertically);
      },
      shouldMove: (clientPosition) =>
        shouldMoveBookingXY({
          clientPosition,
          draggingOriginClick: draggingOriginClick,
          draggedDays,
          rowRef: getSourceRowWithRef()?.rowRef ?? undefined,
          days
        })
    }
  );

  const { handleMouseDown: handleMouseDownStartMode, isDragging: isDraggingStartMode } = useDragAndDrop({
    draggableElementRef: leftEdgeRef,
    onDragStart: (initialPosition) => {
      rowsWithRefsRef.current = getRowsWithRefs();
      draggingMode.current = DnDMode.StartEdge;
      handleDragStart(initialPosition);
    },
    onDragMove: (clientPosition) => {
      const { canDragHorizontally, canDragVertically } = canDragBooking({
        isAuditBooking,
        sourceRowWithRef: getSourceRowWithRef(),
        targetRowRef: targetRowRef.current,
        draggedDays: draggedDays.current,
        canDragVerticallyBaseCheck
      });

      handleDragMove(clientPosition, canDragHorizontally, canDragVertically);
    },
    onDrop: (clientPosition) => {
      datadogRum.addAction(t("dragAndDropBooking.bookingChangeStart", { lng: "en" }));
      handleDrop(clientPosition, DnDMode.StartEdge);
    },
    customDragOperation: (clientPosition) => updateBookingHorizontalPosition(clientPosition, true),
    shouldMove: (clientPosition) =>
      shouldMoveBookingHorizontally({
        clientPosition,
        draggingOriginClick: draggingOriginClick,
        draggedDays,
        rowRef: getSourceRowWithRef()?.rowRef ?? undefined,
        days
      })
  });

  const { handleMouseDown: handleMouseDownEndMode, isDragging: isDraggingEndMode } = useDragAndDrop({
    draggableElementRef: rightEdgeRef,
    onDragStart: (initialPosition) => {
      rowsWithRefsRef.current = getRowsWithRefs();
      draggingMode.current = DnDMode.EndEdge;
      handleDragStart(initialPosition);
    },
    onDragMove: (clientPosition) => {
      const { canDragHorizontally, canDragVertically } = canDragBooking({
        isAuditBooking,
        sourceRowWithRef: getSourceRowWithRef(),
        targetRowRef: targetRowRef.current,
        draggedDays: draggedDays.current,
        canDragVerticallyBaseCheck
      });

      handleDragMove(clientPosition, canDragHorizontally, canDragVertically);
    },
    onDrop: (clientPosition) => {
      datadogRum.addAction(t("dragAndDropBooking.bookingChangeEnd", { lng: "en" }));
      handleDrop(clientPosition, DnDMode.EndEdge);
    },
    customDragOperation: (clientPosition) => updateBookingHorizontalPosition(clientPosition, true),
    shouldMove: (clientPosition) =>
      shouldMoveBookingHorizontally({
        clientPosition,
        draggingOriginClick: draggingOriginClick,
        draggedDays,
        rowRef: getSourceRowWithRef()?.rowRef ?? undefined,
        days
      })
  });

  const isDragging = isDraggingStartEndMode || isDraggingStartMode || isDraggingEndMode;

  return {
    // eslint-disable-next-line react-hooks/refs
    draggingMode: isDragging ? draggingMode.current : null,
    handleMouseDownStartEndMode,
    handleMouseDownStartMode,
    handleMouseDownEndMode,
    // eslint-disable-next-line react-hooks/refs
    start: formatDatabaseDateTime(draggingDates.current.start.getTime()),
    // eslint-disable-next-line react-hooks/refs
    end: formatDatabaseDateTime(draggingDates.current.end.getTime()),
    targetRow: targetRowProfile
  };
};
